/* MailBakes - configurator events for Klaviyo.
   ---------------------------------------------------------------------------
   Listens to the events assets/mb-flow.js dispatches on document and turns
   them into Klaviyo onsite tracking calls. The event names, the payload
   fields and the dedupe rules here are the spec every theme has to match.

     mb:flow:step      -> "Started Configurator" (first time only)
                          "Configurator Step Completed"
                          + profile property last_card_restore_url
                            (every completed step, whenever the link changed)
     mb:flow:added     -> "Card Added to Basket"
                          + profile property last_card_restore_url
     mb:flow:restored  -> nothing sent; marks the session as already started

   Klaviyo's own "Added to Cart" comes from the Shopify server pixel and
   cannot be changed from the theme, so Card Added to Basket is the event
   that carries the restore link. Onsite events are only recorded for
   browsers Klaviyo has already identified (form, checkout, account or an
   email link) and, on this site, only after the cookie banner is accepted.

   Card Added to Basket is queued in sessionStorage and sent on the NEXT page
   (the basket), because the flow navigates away the moment the add succeeds
   and a request fired on the way out is not guaranteed to leave the browser.

   Never sends the card message, the font, the ink colour or the photo as a
   property. The message travels only inside restore_url (link v2). */
(function () {
  'use strict';

  var PENDING_KEY = 'mb_kl_pending';
  var STARTED_KEY = 'mb_kl_started:';
  var fired = {};   /* step_number -> fingerprint of the selections it was sent with */
  var lastRestore = null;   /* last_card_restore_url most recently set on the profile from this page */

  function kl() {
    window.klaviyo = window.klaviyo || [];
    return window.klaviyo;
  }

  function track(name, props) {
    try { kl().push(['track', name, props]); } catch (e) { /* never break the flow */ }
  }

  /* A profile property with no email attached only lands on a browser
     Klaviyo already knows. Anonymous browsers are skipped rather than sent
     an identify call that would be dropped anyway. */
  function setProfile(props) {
    var k = kl();
    try {
      if (typeof k.isIdentified === 'function') {
        k.isIdentified().then(function (yes) {
          if (yes) k.push(['identify', props]);
        }).catch(function () { });
      } else {
        k.push(['identify', props]);
      }
    } catch (e) { /* never break the flow */ }
  }

  function ss(get, key, val) {
    try {
      if (get) return window.sessionStorage.getItem(key);
      if (val === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, val);
    } catch (e) { return null; }
    return null;
  }

  /* ---- payloads --------------------------------------------------------- */

  function base(d) {
    var p = d.product || {};
    return {
      product_id: p.id,
      product_handle: p.handle,
      product_title: p.title,
      product_url: p.url,
      image_url: p.image_url,
      price: p.price,
      collections: (p.collections || []).map(function (c) { return c.handle; }),
      collection_titles: (p.collections || []).map(function (c) { return c.title; }),
      restore_url: d.restore_url
    };
  }

  function selections(d) {
    var s = d.summary || {};
    return {
      card_quantity: s.card_quantity || 1,
      size: s.size,
      slice_count: s.slice_count,
      flavours: s.flavours || [],
      delivery_when: s.delivery_when,
      delivery_date: s.delivery_date,
      postage: s.postage,
      postage_price: s.postage_price,
      extras: (s.extras || []).map(function (x) {
        return x.quantity > 1 ? x.title + ' x ' + x.quantity : x.title;
      }),
      extras_count: s.extras_count,
      total: s.total
    };
  }

  function merge() {
    var out = {};
    for (var i = 0; i < arguments.length; i++) {
      var o = arguments[i] || {};
      for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) out[k] = o[k];
    }
    return out;
  }

  /* What each step is allowed to be judged on. Going back to change the
     flavours and continuing again re-sends the flavours step, because the
     selection changed; walking back and forward without changing anything
     sends nothing. */
  var FIELDS = {
    1: ['size', 'slice_count'],
    2: ['size', 'slice_count', 'flavours'],
    3: ['size', 'slice_count', 'flavours'],
    4: ['size', 'slice_count', 'flavours', 'delivery_when', 'delivery_date'],
    5: ['size', 'slice_count', 'flavours', 'delivery_when', 'delivery_date', 'postage'],
    6: ['size', 'slice_count', 'flavours', 'delivery_when', 'delivery_date', 'postage', 'extras']
  };

  function fingerprint(n, sel) {
    var keys = (FIELDS[n] || Object.keys(sel)).concat(['card_quantity']);
    return JSON.stringify(keys.map(function (k) { return sel[k]; }));
  }

  /* ---- the events --------------------------------------------------------- */

  function startedKey(d) {
    return STARTED_KEY + ((d.product && d.product.handle) || '');
  }

  /* A restored card was started in an earlier session; it is not started
     again here, but every step they now complete is still reported. The
     flow restores before this file has loaded, so the attribute it leaves
     on its root is read as well as the event. */
  document.addEventListener('mb:flow:restored', function (e) {
    var d = (e && e.detail) || {};
    ss(false, startedKey(d), '1');
  });
  (function alreadyRestored() {
    var flow = document.querySelector('[data-mb-flow][data-mb-restored]');
    if (!flow) return;
    ss(false, STARTED_KEY + flow.getAttribute('data-mb-restored'), '1');
  })();

  document.addEventListener('mb:flow:step', function (e) {
    var d = (e && e.detail) || {};
    if (!d.step_number) return;
    var b = base(d);
    var sel = selections(d);

    if (!ss(true, startedKey(d))) {
      ss(false, startedKey(d), '1');
      track('Started Configurator', b);
    }

    var fp = fingerprint(d.step_number, sel);
    if (fired[d.step_number] !== fp) {
      fired[d.step_number] = fp;
      track('Configurator Step Completed', merge(b, {
        step_name: d.step_name,
        step_number: d.step_number
      }, sel));
    }

    /* Keep the profile's restore link as fresh as the furthest step, so an
       abandoned-configurator email lands on the card as it was left, not
       only once it reached the basket. Judged on the link itself rather
       than the step fingerprint: the link also carries the message and the
       highest step reached (st), which the fingerprint deliberately leaves
       out. Same rules as the basket write: identified browsers only. */
    if (d.restore_url && d.restore_url !== lastRestore) {
      lastRestore = d.restore_url;
      setProfile({ last_card_restore_url: d.restore_url });
    }
  });

  document.addEventListener('mb:flow:added', function (e) {
    var d = (e && e.detail) || {};
    var props = merge(base(d), selections(d), {
      $value: (d.summary && d.summary.total) || 0,
      cart_line_key: d.cart_line_key || null
    });
    var queue = [['track', 'Card Added to Basket', props]];
    if (d.restore_url) queue.push(['identify', { last_card_restore_url: d.restore_url }]);
    var ok = false;
    try {
      window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(queue));
      ok = true;
    } catch (err) { ok = false; }
    /* No sessionStorage (private mode on old Safari): send now and hope. */
    if (!ok) flush(queue);
  });

  function flush(queue) {
    (queue || []).forEach(function (item) {
      if (!item || !item[0]) return;
      if (item[0] === 'track') track(item[1], item[2]);
      else if (item[0] === 'identify') setProfile(item[1]);
    });
  }

  /* Anything left over from the page before - the add-to-basket event. */
  (function flushPending() {
    var raw = ss(true, PENDING_KEY);
    if (!raw) return;
    ss(false, PENDING_KEY, null);
    var queue;
    try { queue = JSON.parse(raw); } catch (e) { return; }
    flush(queue);
  })();
})();
